import { useEffect, useState } from 'react';

const STORAGE_KEY = 'apetech-pm:ui-zoom';
const STEPS = [80, 90, 100, 110, 125, 150] as const;
const DEFAULT_ZOOM = 100;
const BASE_PX = 16;

function readStored(): number {
  if (typeof window === 'undefined') return DEFAULT_ZOOM;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const n = raw ? Number(raw) : NaN;
  return STEPS.includes(n as (typeof STEPS)[number]) ? n : DEFAULT_ZOOM;
}

function applyZoom(zoom: number) {
  document.documentElement.style.fontSize = `${(zoom / 100) * BASE_PX}px`;
}

export function UiZoomControl() {
  const [zoom, setZoom] = useState<number>(() => readStored());

  useEffect(() => {
    applyZoom(zoom);
    window.localStorage.setItem(STORAGE_KEY, String(zoom));
  }, [zoom]);

  const idx = STEPS.indexOf(zoom as (typeof STEPS)[number]);
  const canDec = idx > 0;
  const canInc = idx >= 0 && idx < STEPS.length - 1;

  function dec() {
    const next = STEPS[idx - 1];
    if (next !== undefined) setZoom(next);
  }
  function inc() {
    const next = STEPS[idx + 1];
    if (next !== undefined) setZoom(next);
  }
  function reset() {
    setZoom(DEFAULT_ZOOM);
  }

  return (
    <div
      role="group"
      aria-label="UI zoom"
      className="fixed bottom-3 right-3 z-40 flex items-center gap-0.5 rounded-full border border-border/60 bg-popover/85 px-1 py-1 text-popover-foreground shadow-lg backdrop-blur"
      style={{ fontSize: '13px' }}
    >
      <button
        type="button"
        onClick={dec}
        disabled={!canDec}
        aria-label="Decrease UI size"
        title="Decrease UI size"
        className="grid size-7 place-items-center rounded-full text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
      <button
        type="button"
        onClick={reset}
        aria-label={`Reset UI size (current ${zoom}%)`}
        title="Reset to 100%"
        aria-live="polite"
        className="min-w-[3ch] rounded-full px-2 py-0.5 text-center font-mono text-[11px] tabular-nums tracking-tight text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
      >
        {zoom}%
      </button>
      <button
        type="button"
        onClick={inc}
        disabled={!canInc}
        aria-label="Increase UI size"
        title="Increase UI size"
        className="grid size-7 place-items-center rounded-full text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-40"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
  );
}
